<template>
  <div class="order__container">

    <div class="order__nav">
      <product-navigator 
        v-model="productSelected"/>
    </div>

    <div class="order__details columns">

      <div class="column is-half">
        <bom-details-selector 
          ref="details-selector"
          :product="productSelected"
          @select="addToOrder"
          @deselect="removeFromOrder"/>
      </div>

      <div class="column is-half">

        <div class="level mb-2">
          <div class="level-left buttons mb-0">
            <button 
              class="button is-primary" 
              @click="addRow()">
              Add unallocated item
            </button>
            <a class="button" 
              v-if="selected.length > 0"
              @click="deleteSelected()">
              <span class="icon is-medium">
                <i class="fas fa-trash has-text-danger"></i>
              </span>
              <span>
                Delete {{ selected.length }} Detail<span v-if="selected.length > 0">s</span>
              </span>
            </a>
          </div>

          <div class="level-right buttons mb-0">
            <button
              :disabled="!valid || saving" 
              @click="saveOrder()"
              class="button is-link"
              :class="{ 'is-loading': saving }">
              Save Order
            </button>
          </div>
        </div>

        <v-form class="mb-4" :valid.sync="valid">
          <div class="columns">
            <div class="column is-half">
              <label class="label">Supplier*</label>
              <v-select
                v-if="suppliers.length > 0"
                required
                item-text="name"
                item-value="id"
                :options="suppliers"
                v-model="order.supplier_id"
              />
            </div>
            <div class="column is-half">
              <label class="label">Currency*</label>
              <v-select
                required
                :options="currencyOptions"
                v-model="order.currency"
              />
            </div>
          </div>
          <div class="field">
            <label class="label">Description</label>
            <v-text-field 
              placeholder="Enter Order Description"
              v-model="order.desc">
            </v-text-field>
          </div>
        </v-form>

        <v-alert 
          class="my-3"
          v-if="orderDetails.length == 0 && orderDetailsBom.length == 0"
          type="info">
          No items selected for this order
        </v-alert>

        <table 
          v-if="orderDetails.length > 0 || orderDetailsBom.length > 0"
          class="table is-hoverable is-fullwidth has-smallish-font">
          <thead>
            <tr>
              <th 
                v-for="(header, headerIndex) in headers" 
                :key="headerIndex">
                {{ header.label }}
              </th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            <tr 
              v-for="(detail, i) in orderDetails"
              :key="`unallocated-${i}`">
              <td>
                <stocking-unit-selector 
                  v-model="detail.stocking_unit_id"/>
              </td>
              <td 
                colspan="3" 
                class="has-text-grey-light has-text-centered">
                Unallocated Item
              </td>
              <td>
                <v-text-field 
                  required
                  placeholder="Enter quantity"
                  v-model="detail.qty_unassigned"/>
              </td>
              <td>
                <v-text-field 
                  required
                  type="number"
                  placeholder="Price"
                  v-model="detail.total_price"/>
              </td>
              <td>
                <div class="buttons buttons-right mt-1">
                  <button  
                    @click="removeRow(detail.id)"
                    class="button is-small">
                    <span class="has-text-grey icon">
                      <i class="fas fa-trash"></i>
                    </span>
                  </button>
                </div>
              </td>
            </tr>

            <!-- bom details -->
            <tr 
              class="v-centered"
              v-for="detail in orderDetailsBom"
              :key="detail.id">
              <td>
                {{ detail.product_master.name }}
              </td>
              <td>
                {{ detail.bom.project.project_name }}
              </td>
              <td>
                <router-link 
                  :to="{ 
                    name: 'BomViewer', 
                    params: { bomId: detail.bom.id, bom: detail.bom }}">
                  {{ detail.bom.name }}
                </router-link>
              </td>
              <td>
                {{ detail.finished_qty }}
              </td>
              <td>
                <v-text-field 
                  required
                  type="number"
                  placeholder="Order quantity"
                  v-model="detail.qty_to_be_ordered"
                  :rule="[v => v.qty_to_be_ordered >= v.finished_qty ? true : 
                    'Order qty needs to be greater than qty required by BOM']"/>
              </td>
              <td>
                <v-text-field 
                  required
                  type="number"
                  placeholder="Price"
                  v-model="detail.total_price"/>
              </td>
              <td>
                <div class="buttons buttons-right">
                  <button  
                    @click="removeBomDetail(detail.id)"
                    class="button is-small">
                    <span class="has-text-grey icon">
                      <i class="fas fa-trash"></i>
                    </span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

      </div>

    </div>

  </div>
</template>

<script>
import VForm from '@/components/base/VForm'
import VSelect from '@/components/base/VSelect'
import VTextField from '@/components/base/VTextField'
import VAlert from '@/components/base/VAlert'
// import VCheckbox from '@/components/base/VCheckbox'
import BomDetailsSelector from '@/components/inventory/BomDetailsSelector'
import ProductNavigator from '@/components/inventory/ProductNavigator'
import StockingUnitSelector from '@/components/inventory/StockingUnitSelector'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VSelect,
    VTextField,
    VForm,
    VAlert,
    // VCheckbox,
    BomDetailsSelector,
    ProductNavigator,
    StockingUnitSelector
  },
  data() {
    return {
      productSelected: {
        product_master_id: null,
        stocking_unit_id: null
      },
      order: {
        supplier_id: null,
        desc: null,
        currency: null
      },
      orderDetails: [],
      orderDetailsBom: [],
      selected: [],
      allSelected: false,
      headers: [
        { label: 'Product', field: 'product_master_id', default: null },
        { label: 'Project' },
        { label: 'BOM' },
        { label: 'Qty', field: 'qty_unassigned', default: null },
        { label: 'Order Qty' },
        { label: 'Price' }
      ],
      valid: false,
      saving: false
    }
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapState("orders", ["currencyOptions"]),
    ...mapState("suppliers", ["suppliers"]),
    ...mapState("products", ["products"]),
    template() {
      let obj = {};
      this.headers.forEach(col => {
        obj[col.field] = col.default;
      });

      return obj
    },
    ids() {
      return this.orderDetails.map(d => d.id);
    }
  },
  methods: {
    ...mapActions("orders", ["addOrder", "addDetail", "addBomDetail"]),
    ...mapActions("suppliers", ["getSuppliers"]),
    ...mapActions("products", ["getProducts"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.suppliers.length == 0) {
        this.getSuppliers({ per_page: 1000 });
      }

      if (this.products.length == 0) {
        this.getProducts({ per_page: 1000 });
      }
    },
    addToOrder(detail) {
      this.orderDetailsBom.push(detail);
    },
    removeFromOrder(detailId) {
      this.orderDetailsBom.forEach((detail, i) => {
        if (detail.id == detailId) {
          this.orderDetailsBom.splice(i);
        }
      })
    },
    addRow() {
      let newRow = Object.assign({}, this.template);
      newRow.id = this.ids.length == 0 ? 1 : Math.max(...this.ids) + 1;

      this.orderDetails.push(newRow);
    },
    removeRow(id) {
      this.orderDetails.forEach((detail, i) => {
        if (detail.id == id) {
          this.orderDetails.splice(i, 1);
        }
      })
    },
    removeBomDetail(id) {
      this.$refs['details-selector'].deselect(id);
    },
    // deleteSelected() {
    //   this.selected.forEach((id, i) => {
    //     this.orderDetails.forEach(detail => {
    //       if (detail.id == id) {
    //         this.orderDetails.splice(i, 1);
    //       }
    //     });
    //   });
    //   this.selected.splice(0, this.selected.length);
    // },
    // toggleSelected(id) {
    //   const index = this.selected.indexOf(id);
    //   if (index == -1) {
    //     this.selected.push(id);
    //   } else {
    //     this.selected.splice(index, 1);
    //     if (this.selected.length == 0) {
    //       this.allSelected = false;
    //     }
    //   }
    // },
    // toggleAllSelected(v) {
    //   this.allSelected = v;
    //   const { selected } = this;

    //   if (v) {
    //     this.detailIds.forEach(id => {
    //       if (selected.indexOf(id) == -1) {
    //         selected.push(id);
    //       }
    //     })
    //   } else {
    //     selected.splice(0, selected.length);
    //   }
    // },
    saveOrder() {
      this.saving = true;
      this.addOrder({ data: this.order })
        .then(resp => {
          if (
            this.orderDetails.length > 0 || 
            this.orderDetailsBom.length > 0
          ) {
            this.saveOrderDetails(resp.data.id);
          } else {
            this.saving = false;
            this.notifySuccess();
          }
        }).catch(() => this.saving = false);
    },
    saveOrderDetails(orderId) {
      let completedDetails = this.orderDetails.filter(
        d => Object.values(d).reduce((a, b) => { 
          if (b == null) { a += 1 } 
        }, 0) != Object.values.length
      )

      let orderDetailsBom = this.orderDetailsBom.map(d => {
        return {
          order_id: orderId,
          bom_detail_id: d.id,
          qty_to_be_ordered: d.qty_to_be_ordered
        }
      })

      let promises = [];

      completedDetails.forEach(detail => {
        detail.order_id = orderId;
        promises.push(this.addDetail(detail));
      });

      orderDetailsBom.forEach(detail => {
        promises.push(this.addBomDetail(detail));
      })
      
      Promise.all(promises).then(() => {
        this.notifySuccess();
      }).catch(() => this.saving = false);
    },
    notifySuccess() {
      this.saving = false;

      this.setNotification({
        type: 'success',
        status: true,
        message: 'Order successfully added'
      });
      this.$router.push({ name: 'Orders' });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.order {
  &__container {
    display: flex;
    flex-direction: row;
  }
  &__nav {
    padding: 1em;
    width: 20%;
  }
  &__details {
    padding-top: 1em;
    padding-bottom: 1em;
    width: 80%;
  }
}
</style>